import PageHeader from "../components/PageHeader";
import FormFrame from "../components/FormFrame";
import { Reveal } from "../components/Reveal";
import { IMAGES, GHL_FORMS, CONTACT } from "../data/site";
import { MapPin, Phone, Mail, ArrowUpRight, Instagram } from "lucide-react";

const MAP_EMBED =
  "https://www.google.com/maps?q=Vista+Banquet+Hall+of+Alexandria,+7846+Richmond+Highway,+Alexandria,+VA+22306&output=embed";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHeader
        overline="Get In Touch"
        titleLines={["Contact Vista", <span key="i" className="italic text-gold">Banquet Hall</span>]}
        subtitle="We'd love to hear about your event. Reach out with any question — our team responds within one business day."
        image={IMAGES.rosesBouquet}
        imageAlt="Blush wedding florals"
        testId="contact-header"
      />

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-16 md:pb-24">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16 items-start">
          {/* info */}
          <div className="lg:col-span-5">
            <Reveal>
              <span className="overline text-gold">Details</span>
              <h2 className="mt-5 font-serif text-3xl md:text-4xl font-light tracking-tight text-navy">
                Visit, call, or write to us.
              </h2>
            </Reveal>

            <div className="mt-10 space-y-8">
              <Reveal>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold"><MapPin size={18} /></span>
                  <div>
                    <p className="overline text-navy/50">Address</p>
                    <a href={CONTACT.mapUrl} target="_blank" rel="noreferrer" data-testid="contact-address" className="mt-1 block font-light text-navy hover:text-gold transition-colors">
                      {CONTACT.addressLine1}, {CONTACT.addressLine2}
                    </a>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold"><Phone size={18} /></span>
                  <div>
                    <p className="overline text-navy/50">Phone</p>
                    <a href={CONTACT.phoneHref} data-testid="contact-phone" className="mt-1 block font-light text-navy hover:text-gold transition-colors">{CONTACT.phone}</a>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold"><Mail size={18} /></span>
                  <div>
                    <p className="overline text-navy/50">Email</p>
                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="mt-1 block font-light text-navy hover:text-gold transition-colors">{CONTACT.email}</a>
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.24}>
              <div className="mt-10">
                <p className="overline text-navy/50 mb-4">Featured On</p>
                <div className="flex flex-wrap gap-4">
                  <a href={CONTACT.theKnot} target="_blank" rel="noreferrer" data-testid="contact-theknot" className="btn-outline text-navy hover:bg-navy hover:text-ivory text-xs px-6 py-3">
                    The Knot <ArrowUpRight size={14} className="ml-1" />
                  </a>
                  <a href={CONTACT.weddingWire} target="_blank" rel="noreferrer" data-testid="contact-weddingwire" className="btn-outline text-navy hover:bg-navy hover:text-ivory text-xs px-6 py-3">
                    WeddingWire <ArrowUpRight size={14} className="ml-1" />
                  </a>
                  <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="btn-outline text-navy hover:bg-navy hover:text-ivory text-xs px-6 py-3">
                    <Instagram size={14} className="mr-1" /> Instagram
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          {/* form */}
          <div className="lg:col-span-7">
            <FormFrame
              overline="Contact Form"
              title="Send Us a Message"
              formId={GHL_FORMS.contact.id}
              formName={GHL_FORMS.contact.name}
              height={GHL_FORMS.contact.height}
              testId="contact-form"
            />
          </div>
        </div>
      </section>

      {/* map */}
      <section data-testid="contact-map" className="relative">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24">
          <div className="overflow-hidden rounded-sm border border-gold/40">
            <iframe
              title="Vista Banquet Hall location"
              src={MAP_EMBED}
              className="h-[420px] w-full grayscale-[0.2]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
